import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users } from 'phosphor-react';
import { personalInfoSectionStyles as styles } from '../styles/commonStyles';
import { SelectField, SelectOption } from './SelectField';
import { FAMILY_COMPOSITION_VALUES } from '../utils/familyComposition';

interface PersonalInfoSectionProps {
  value: string;
  onChange: (value: string) => void;
  options?: SelectOption[];
  id?: string;
  title?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | null;
  sideField?: React.ReactNode;
  hideFamilyField?: boolean;
  children?: React.ReactNode;
}

export const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  value,
  onChange,
  options,
  id = 'family-composition',
  title,
  label,
  placeholder,
  required = true,
  disabled = false,
  error = null,
  sideField,
  hideFamilyField = false,
  children,
}) => {
  const { t } = useTranslation();
  const resolvedOptions = options ?? FAMILY_COMPOSITION_VALUES.map((v) => ({
    value: v,
    label: t(`common.familyComposition.${v}`),
  }));
  const familyField = (
    <SelectField
      id={id}
      value={value}
      onChange={onChange}
      options={resolvedOptions}
      label={label ?? t('common.personalInfo.familyComposition', 'Gezinssamenstelling')}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      error={error}
    />
  );
  // When the only field is hidden (gezinssamenstelling reused from an earlier product) and there is
  // no side field or extra content, render nothing instead of an empty "Persoonlijke gegevens" box.
  if (hideFamilyField && !sideField && !children) return null;
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.iconContainer}>
          <Users size={28} weight="duotone" color="var(--secondary-color, #F51C17)" />
        </div>
        <h2 style={styles.title}>
          {title ?? t('common.personalInfo.title', 'Persoonlijke gegevens')}
        </h2>
      </div>
      {hideFamilyField ? (
        sideField ?? null
      ) : sideField ? (
        <>
          <div className="sl-pi-fields-row">
            {familyField}
            {sideField}
          </div>
        </>
      ) : (
        familyField
      )}
      {children}
    </div>
  );
};

export default PersonalInfoSection;
